import React from 'react';
import { useNavigate } from 'react-router-dom';
import { surveyScopeLabel } from '../utils/surveyScope';
import { Box, Button, Card, CardContent, Chip, LinearProgress, Paper, Typography } from '@mui/material';
import { Assignment as SurveyIcon } from '@mui/icons-material';

const SURVEYS_ROUTE = '/drew/surveys';

/**
 * "Active Surveys" card of the OE Dashboard (moved out of DriftDashboard.jsx for P3-20).
 * `compact` (P3-02, C0820 "takes too much real estate"): at most `maxItems` rows, inline
 * progress, no description, "View all".
 */
export function ActiveSurveysCard({ activeSurveys = [], surveys = [], responseCounts = {}, compact = false, maxItems = 3, organization = null }) {
  const navigate = useNavigate();
  const rows = compact ? activeSurveys.slice(0, maxItems) : activeSurveys;
  const hidden = activeSurveys.length - rows.length;

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: compact ? 1 : 2 }}>
          <SurveyIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">Active Surveys</Typography>
        </Box>
        {activeSurveys.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: compact ? 1 : 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No active surveys right now.
            </Typography>
            <Button variant="outlined" size="small" onClick={() => navigate(SURVEYS_ROUTE)}>
              View Surveys
            </Button>
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {rows.map((survey) => {
              const received = responseCounts[survey.id] || 0;
              const expected = survey.targetResponses || null;
              const progressPct = expected ? Math.min((received / expected) * 100, 100) : null;
              const progressColor = progressPct !== null ? (progressPct >= 100 ? 'success' : progressPct >= 50 ? 'primary' : 'warning') : 'primary';
              return (
                <Paper
                  key={survey.id}
                  variant="outlined"
                  sx={{ p: compact ? 1.25 : 2, mb: compact ? 1 : 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 2 }}
                  onClick={() => navigate(`${SURVEYS_ROUTE}/${survey.id}`)}
                >
                  <Typography variant="subtitle2" fontWeight={600} noWrap>
                    {survey.title}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                    <Chip label="Active" size="small" color="success" variant="outlined" />
                    <Chip label={surveyScopeLabel(survey, organization)} size="small" variant="outlined" title="Who this survey is for (P3-03)" />
                    <Chip
                      label={expected ? `${received}/${expected} responses` : `${received} responses`}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: '0.7rem' }}
                    />
                    {survey.dueDate && (
                      <Typography variant="caption" color="text.secondary">
                        Due: {survey.dueDate}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ mt: compact ? 1 : 1.5 }}>
                    {!compact && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Response Progress
                        </Typography>
                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                          {expected ? `${received} of ${expected}` : `${received} received`}
                          {progressPct !== null && ` (${Math.round(progressPct)}%)`}
                        </Typography>
                      </Box>
                    )}
                    <LinearProgress
                      variant="determinate"
                      value={progressPct !== null ? progressPct : Math.min(received * 10, 100)}
                      color={progressColor}
                      sx={{ height: compact ? 6 : 8, borderRadius: 4, bgcolor: 'action.hover' }}
                    />
                  </Box>
                  {!compact && survey.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {survey.description}
                    </Typography>
                  )}
                </Paper>
              );
            })}
            <Button variant="text" size="small" onClick={() => navigate(SURVEYS_ROUTE)} sx={{ mt: 0.5 }}>
              {hidden > 0 ? `View all (${activeSurveys.length} active)` : `View All Surveys (${surveys.length})`}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default ActiveSurveysCard;
